import React from "react";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import FaqSection from "../components/FaqSection";
import { absoluteUrl } from "../lib/seo";
import { fastFaqs, fastProduct } from "../content/discoveryContent";

const softwareSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: fastProduct.name,
  applicationCategory: fastProduct.category,
  operatingSystem: "Web",
  url: absoluteUrl(fastProduct.slug),
  sameAs: fastProduct.externalUrl,
  description: fastProduct.description,
  creator: {
    "@type": "Organization",
    name: "Drishti Technologies",
    url: "https://drishtitech.in",
  },
  offers: {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    price: "0",
    priceCurrency: "INR",
    description: "Contact Drishti Technologies for rollout and access details.",
  },
  featureList: fastProduct.capabilities,
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: absoluteUrl("/"),
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Products",
      item: absoluteUrl("/products/fast"),
    },
    {
      "@type": "ListItem",
      position: 3,
      name: "FAST",
      item: absoluteUrl("/products/fast"),
    },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: fastFaqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};

const FastProductPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Seo
        title={fastProduct.seoTitle}
        description={fastProduct.seoDescription}
        path={fastProduct.slug}
        keywords={fastProduct.keywords}
        jsonLd={[softwareSchema, breadcrumbSchema, faqSchema]}
      />
      <Header />

      <main className="pt-20">
        <section className="bg-gradient-to-b from-black via-zinc-950 to-black">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-20 lg:py-24">
            <div className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-10 items-start">
              <div>
                <p className="text-xs tracking-[0.35em] uppercase text-red-500 mb-4">
                  {fastProduct.eyebrow}
                </p>
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight mb-6">
                  {fastProduct.title}
                </h1>
                <p className="text-xl text-white/75 leading-relaxed mb-6 max-w-3xl">
                  {fastProduct.description}
                </p>
                <p className="text-white/60 leading-relaxed max-w-3xl">
                  FAST is positioned for businesses that want accounting workflow software with clearer operational flow from source document to report output, instead of fragmented handoffs across spreadsheets, email and disconnected bookkeeping steps.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mt-10">
                  <a
                    href={fastProduct.externalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-red-600 text-black px-8 py-4 text-sm font-medium tracking-[0.18em] uppercase transition-all duration-400 hover:bg-white hover:text-black"
                    style={{ borderRadius: "0px" }}
                  >
                    View Product
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                  <a
                    href="/#contact"
                    className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 text-sm font-medium tracking-[0.18em] uppercase transition-all duration-400 hover:border-red-500 hover:text-red-400"
                    style={{ borderRadius: "0px" }}
                  >
                    Contact Drishti Tech
                  </a>
                </div>
              </div>

              <aside className="border border-red-600/30 bg-red-600/[0.08] p-8 lg:p-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {fastProduct.heroStats.map((stat) => (
                    <div key={stat.label} className="border border-white/10 bg-black/40 p-5">
                      <div className="text-sm uppercase tracking-[0.2em] text-white/45 mb-2">
                        {stat.label}
                      </div>
                      <div className="text-lg text-white/85 leading-relaxed">
                        {stat.value}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-8 border-t border-white/10">
                  <div className="text-sm uppercase tracking-[0.2em] text-white/45 mb-3">
                    Capability summary
                  </div>
                  <div className="space-y-3">
                    {fastProduct.capabilities.map((item) => (
                      <div key={item} className="flex items-start gap-3 text-white/80">
                        <CheckCircle2 className="w-5 h-5 text-red-500 mt-0.5" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-b from-zinc-950 via-black to-zinc-950">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <p className="text-xs tracking-[0.35em] uppercase text-red-500 mb-4">
                Product Scope
              </p>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                What FAST <span className="text-red-600">actually covers</span>
              </h2>
              <p className="text-xl text-white/70 max-w-3xl mx-auto">
                This is not just a ledger screen. FAST is designed as a document-to-books workflow product with operational checks and reporting built into the same flow.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {fastProduct.features.map((feature) => (
                <article
                  key={feature.title}
                  className="border border-white/15 bg-white/[0.04] p-7 hover:border-red-600/50 transition-all duration-300"
                >
                  <h3 className="text-2xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-white/70 leading-relaxed">{feature.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-b from-black via-zinc-950 to-black">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-10 items-start">
              <div>
                <p className="text-xs tracking-[0.35em] uppercase text-red-500 mb-4">
                  Workflow
                </p>
                <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                  From source document to <span className="text-red-600">accounting output</span>
                </h2>
                <p className="text-white/70 text-lg leading-relaxed max-w-2xl">
                  FAST is easier to evaluate when the operational sequence is clear. The product is designed around actual bookkeeping movement, not isolated feature checkboxes.
                </p>
              </div>
              <div className="space-y-4">
                {fastProduct.workflow.map((step, index) => (
                  <div
                    key={step}
                    className="flex items-start gap-5 border border-white/15 bg-white/[0.03] p-6"
                  >
                    <div className="text-2xl font-bold text-red-500 min-w-8">
                      0{index + 1}
                    </div>
                    <p className="text-white/80 text-lg leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-b from-zinc-950 via-black to-zinc-950">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="border border-white/15 bg-white/[0.04] p-8 lg:p-10">
                <p className="text-xs tracking-[0.35em] uppercase text-red-500 mb-4">
                  Who It Is For
                </p>
                <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                  Teams that need cleaner accounting operations
                </h2>
                <div className="space-y-4">
                  {fastProduct.audiences.map((item) => (
                    <div key={item} className="flex items-start gap-3 text-white/80">
                      <CheckCircle2 className="w-5 h-5 text-red-500 mt-0.5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-white/15 bg-white/[0.04] p-8 lg:p-10">
                <p className="text-xs tracking-[0.35em] uppercase text-red-500 mb-4">
                  Why It Matters
                </p>
                <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                  Better reviewability, less operational drag
                </h2>
                <div className="space-y-4">
                  {fastProduct.whyItMatters.map((item) => (
                    <div key={item} className="flex items-start gap-3 text-white/80">
                      <CheckCircle2 className="w-5 h-5 text-red-500 mt-0.5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <FaqSection
          id="fast-faq"
          eyebrow="FAST FAQ"
          title="Plain-language answers for evaluators and search systems"
          intro="This FAQ is intentionally factual and concise so people, search engines and AI answer engines can classify the product quickly."
          faqs={fastFaqs}
          pageLink={{ href: "/faq", label: "Open all FAQs" }}
        />

        <section className="py-20 bg-black">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
            <div className="border border-red-600/30 bg-red-600/[0.08] p-10 lg:p-12 text-center">
              <p className="text-xs tracking-[0.35em] uppercase text-red-400 mb-4">
                Live Product
              </p>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Explore FAST or discuss fit with Drishti Technologies
              </h2>
              <p className="text-white/75 text-lg leading-relaxed max-w-3xl mx-auto mb-8">
                FAST is already live. Use the product link for a direct look, then contact Drishti Technologies if you want to discuss how the workflow maps to your finance operations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={fastProduct.externalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-red-600 text-black px-8 py-4 text-sm font-medium tracking-[0.18em] uppercase transition-all duration-400 hover:bg-white hover:text-black"
                  style={{ borderRadius: "0px" }}
                >
                  Open FAST
                  <ArrowUpRight className="w-4 h-4" />
                </a>
                <a
                  href="/#contact"
                  className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 text-sm font-medium tracking-[0.18em] uppercase transition-all duration-400 hover:border-red-500 hover:text-red-400"
                  style={{ borderRadius: "0px" }}
                >
                  Talk to Drishti Tech
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FastProductPage;
